import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Button } from '../components/ui';
import SponsoredCoursesRow from '../components/SponsoredCoursesRow';
import useAuthStore from '../store/authStore';
import FeaturedTeachersCarousel from '../components/FeaturedTeachersCarousel';
import useTenantStore from '../store/tenantStore';
import PlatformFooter from '../components/PlatformFooter';
import useInView from '../hooks/useInView';
import CelebrationIntro from '../components/CelebrationIntro';
import useBrandingStore from '../store/brandingStore';

// Shared with Store.js: a visitor tapping "Enroll" here before creating an
// account resumes straight into that course's enrollment flow once they
// register, instead of landing on a generic store page.
const PENDING_ENROLL_KEY = 'lmsid_pending_enroll_module';

// Scroll-reveal wrapper — fades/lifts children in once they enter the
// viewport (see hooks/useInView), with an optional stagger delay so a row
// of cards animates in left-to-right instead of all at once. Falls back to
// fully visible, un-animated for reduced-motion users.
function Reveal({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out motion-reduce:transition-none motion-reduce:transform-none ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
      style={{ transitionDelay: inView ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
}

const ROLE_FEATURES = [
  {
    key: 'teacher',
    color: 'from-primary-500 to-primary-700',
    ring: 'group-hover:ring-primary-200',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.42A12.02 12.02 0 0121 15.5c0 .84-.1 1.66-.29 2.44M12 14L5.84 10.58A12.02 12.02 0 003 15.5c0 2.42 1.03 4.6 2.68 6.13M12 14v7" />
    ),
    title: 'For Teachers',
    items: [
      'Build courses with lessons, videos, PDFs and images',
      'Create quizzes with instant grading',
      'Track every student\'s progress',
      'Message students and parents directly',
    ],
  },
  {
    key: 'student',
    color: 'from-emerald-500 to-emerald-700',
    ring: 'group-hover:ring-emerald-200',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    ),
    title: 'For Students',
    items: [
      'Browse the course store and enroll in one click',
      'Watch video lessons and download materials',
      'Take quizzes and see your results instantly',
      'Ask questions in the course chat',
    ],
  },
  {
    key: 'parent',
    color: 'from-accent-500 to-accent-700',
    ring: 'group-hover:ring-accent-200',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM19 8v4m-2-2h4" />
    ),
    title: 'For Parents',
    items: [
      'Link your account to your child\'s profile',
      'Follow their enrollments and progress',
      'See quiz results as they come in',
      'Message teachers directly',
    ],
  },
  {
    key: 'admin',
    color: 'from-purple-500 to-purple-700',
    ring: 'group-hover:ring-purple-200',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-4-4" />
    ),
    title: 'For Administrators',
    items: [
      'Approve new teacher accounts',
      'Manage every user on the platform',
      'Moderate messages and reported content',
      'Configure subscription plans and grade levels',
    ],
  },
];

const HOW_IT_WORKS = [
  {
    key: 'signup',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM2 20a6 6 0 0112 0v1H2v-1z" />
    ),
    title: 'Create your account',
    desc: 'Sign up as a teacher, student, parent or administrator — the app adapts to your role from the very first screen.',
  },
  {
    key: 'setup',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6h4m6 0a10 10 0 11-20 0 10 10 0 0120 0z" />
    ),
    title: 'Build or enroll',
    desc: 'Teachers put together lessons, quizzes and materials; students enroll and start learning; parents link their child in a couple of taps.',
  },
  {
    key: 'track',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    ),
    title: 'Track everything in one place',
    desc: 'Progress, grades, messages and payments all live on one dashboard — no spreadsheets, no separate apps to check.',
  },
];

const HERO_BADGES = [
  {
    key: 'grading',
    label: 'Instant Quiz Grading',
    color: 'text-primary-600 bg-primary-50',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
  },
  {
    key: 'messaging',
    label: 'Built-in Messaging',
    color: 'text-emerald-600 bg-emerald-50',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />,
  },
  {
    key: 'languages',
    label: 'EN · FR · AR',
    color: 'text-accent-700 bg-accent-50',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />,
  },
];

const SHOWCASE = [
  { file: 'teacher-dashboard.png', title: 'A dashboard for every role', desc: 'Teachers see their modules and quick actions at a glance.' },
  { file: 'module-detail.png', title: 'Real lessons, real video', desc: 'Lessons combine video, text, downloadable materials and quizzes in one place.' },
  { file: 'courses-grid.png', title: 'Browse courses visually', desc: 'Cover photos and clear pricing make courses easy to scan.' },
  { file: 'messages.png', title: 'Built-in messaging', desc: 'Course group chats and private conversations between teachers, students and parents.' },
  { file: 'admin-dashboard.png', title: 'Full admin control', desc: 'Approve teachers, manage users, and moderate the platform from one dashboard.' },
  { file: 'parent-dashboard.png', title: 'Parents stay in the loop', desc: 'Link a child\'s account and follow their courses and progress.' },
];

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isFeatureEnabled = useTenantStore((state) => state.isFeatureEnabled);
  const brandingLoaded = useBrandingStore((state) => state.loaded);
  const celebrationEnabled = useBrandingStore((state) => state.celebrationEnabled);
  const loadBranding = useBrandingStore((state) => state.load);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [activeShowcase, setActiveShowcase] = React.useState(0);
  const [hasFeaturedTeachers, setHasFeaturedTeachers] = React.useState(null);
  const handleFeaturedAvailability = React.useCallback((available) => setHasFeaturedTeachers(available), []);

  React.useEffect(() => { loadBranding(); }, [loadBranding]);

  return (
    <div className="h-screen overflow-y-auto bg-surface-50 text-gray-900">
      {!brandingLoaded && <div className="fixed inset-0 z-[1000] bg-[#211047]" aria-hidden="true" />}
      {brandingLoaded && celebrationEnabled && <CelebrationIntro />}
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-surface-50/90 backdrop-blur border-b border-surface-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size={34} textColor="text-gray-900" />
          <div className="hidden lg:flex items-center gap-6 xl:gap-8 text-sm font-medium text-gray-600">
            <a href="#features" className="hover:text-primary-600 transition">{t('Features')}</a>
            <Link to="/store" className="hover:text-primary-600 transition">{t('Store')}</Link>
            {isFeatureEnabled('teacher_profiles') && <Link to="/teachers" className="hover:text-primary-600 transition">{t('Choose your teacher')}</Link>}
            <a href="#showcase" className="hover:text-primary-600 transition">{t('Tutorial')}</a>
            <a href="#download" className="hover:text-primary-600 transition">{t('Download')}</a>
            {isFeatureEnabled('contact_messages') && <Link to="/contact" className="hover:text-primary-600 transition">{t('Contact us')}</Link>}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')}>{t('Dashboard')}</Button>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline text-sm font-medium text-gray-700 hover:text-primary-600 transition px-2">
                  {t('Login')}
                </Link>
                <Button onClick={() => navigate('/register')}>{t('Get Started')}</Button>
              </>
            )}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="lg:hidden p-2 -mr-1 text-gray-600 hover:bg-gray-100 rounded-lg transition"
              aria-label={t('Menu')}
              aria-expanded={mobileMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-surface-200 bg-surface-50">
            <nav className="max-w-6xl mx-auto px-6 py-3 flex flex-col gap-3 text-sm font-medium text-gray-600">
              <a href="#features" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary-600 transition">{t('Features')}</a>
              <Link to="/store" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary-600 transition">{t('Store')}</Link>
              {isFeatureEnabled('teacher_profiles') && <Link to="/teachers" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary-600 transition">{t('Choose your teacher')}</Link>}
              <a href="#showcase" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary-600 transition">{t('Tutorial')}</a>
              <a href="#download" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary-600 transition">{t('Download')}</a>
              {isFeatureEnabled('contact_messages') && <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary-600 transition">{t('Contact us')}</Link>}
              {!isAuthenticated && (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="sm:hidden hover:text-primary-600 transition">{t('Login')}</Link>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-surface-200">
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="absolute -top-24 -right-24 w-[30rem] h-[30rem] rounded-full bg-primary-300/25 blur-3xl" />
          <div className="absolute -bottom-36 left-1/3 w-[28rem] h-[28rem] rounded-full bg-accent-200/25 blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.2]"
            style={{
              backgroundImage: 'radial-gradient(rgba(103,57,224,0.25) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[1.08fr_0.92fr] items-center gap-12 lg:gap-16">
            <div className="relative min-h-[390px] sm:min-h-[500px] lg:min-h-[570px] order-2 lg:order-1">
              <div className="absolute left-[8%] top-[12%] h-48 w-48 rounded-full bg-primary-200/45 blur-3xl motion-safe:animate-hero-orbit sm:h-64 sm:w-64" aria-hidden="true" />
              <div className="absolute bottom-[8%] right-[8%] h-40 w-40 rounded-full bg-accent-200/40 blur-3xl motion-safe:animate-hero-orbit-reverse sm:h-56 sm:w-56" aria-hidden="true" />
              <div className="absolute inset-x-[-8%] bottom-0 top-6 motion-safe:animate-hero-rise">
                <img
                  src="/showcase/hero-digital-learning-cutout.png"
                  alt={t('A student climbing toward their digital learning future')}
                  className="h-full w-full object-contain object-center drop-shadow-[0_28px_30px_rgba(35,45,105,0.24)] motion-safe:animate-hero-float"
                  width={1448}
                  height={1086}
                />
              </div>

              <div className="absolute -top-5 left-3 sm:left-8 flex items-center gap-3 rounded-2xl bg-white/95 px-4 py-3 shadow-card-hover backdrop-blur ring-1 ring-surface-200 motion-safe:animate-hero-card-float">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </span>
                <span><strong className="block text-sm text-gray-900">{t('Learn at your pace')}</strong><small className="text-xs text-gray-500">{t('Progress saved automatically')}</small></span>
              </div>

              <div className="absolute -bottom-5 right-3 sm:right-8 flex items-center gap-3 rounded-2xl bg-white/95 px-4 py-3 shadow-card-hover backdrop-blur ring-1 ring-surface-200 motion-safe:animate-hero-card-float-delayed">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-50 text-accent-600">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M5 12a7 7 0 1014 0 7 7 0 00-14 0z" /></svg>
                </span>
                <span><strong className="block text-sm text-gray-900">{t('All in one place')}</strong><small className="text-xs text-gray-500">{t('Courses, quizzes & progress')}</small></span>
              </div>
            </div>

            <div className="order-1 lg:order-2 text-center lg:text-left rtl:lg:text-right">
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-2 text-xs font-bold uppercase tracking-[0.14em] text-primary-700 shadow-card ring-1 ring-primary-100">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75 motion-reduce:animate-none" /><span className="relative inline-flex h-2 w-2 rounded-full bg-primary-600" /></span>
                {t('A smarter way to learn')}
              </span>
              <h1 className="mt-6 text-4xl font-display font-bold leading-[1.08] tracking-tight text-gray-900 sm:text-5xl lg:text-[3.5rem] text-balance">
                <span className="bg-gradient-to-r from-primary-600 via-primary-500 to-accent-500 bg-clip-text text-transparent">
                  {t('HeroBrandName')}
                </span>{' '}
                {t('brings everyone together in one learning journey.')}
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-base leading-8 text-gray-600 sm:text-lg lg:mx-0">
                {t('Teachers build courses, students learn at their pace, parents follow along, admins keep it all running.')}
              </p>

              <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center lg:justify-start">
                {isAuthenticated ? (
                  <Button size="lg" onClick={() => navigate('/dashboard')} className="shadow-card-hover">{t('Go to Dashboard')}</Button>
                ) : (
                  <>
                    <Button size="lg" onClick={() => navigate('/register')} className="shadow-card-hover">{t('Create Free Account')}</Button>
                    <Button size="lg" variant="secondary" onClick={() => navigate('/login')}>{t('Login')}</Button>
                  </>
                )}
              </div>

              <div className="mt-8 grid grid-cols-3 gap-2 sm:gap-3">
                {HERO_BADGES.map((badge) => (
                  <div key={badge.key} className="flex min-w-0 flex-col items-center gap-2 rounded-2xl bg-white/75 px-2 py-3 text-center shadow-card ring-1 ring-surface-200 backdrop-blur sm:flex-row sm:px-3 sm:text-left">
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${badge.color}`}>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">{badge.icon}</svg>
                    </span>
                    <span className="text-[11px] font-semibold leading-tight text-gray-700 sm:text-xs">{t(badge.label)}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500 lg:justify-start">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                {t('Available in English, Français and العربية')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 border-t border-surface-200 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-16">
            <h2 className="text-3xl font-display font-bold tracking-tight">{t('Up and running in three steps')}</h2>
            <p className="mt-3 text-gray-600 max-w-xl mx-auto">{t('No onboarding calls, no setup fees — just sign up and go.')}</p>
          </Reveal>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {/* Connecting line behind the steps, desktop only */}
            <div className="hidden md:block absolute top-7 left-[16.5%] right-[16.5%] h-px bg-gradient-to-r from-primary-200 via-accent-200 to-emerald-200" aria-hidden="true" />
            {HOW_IT_WORKS.map((step, i) => (
              <Reveal key={step.key} delay={i * 120} className="relative text-center flex flex-col items-center">
                <div className="w-14 h-14 rounded-2xl bg-white border-2 border-primary-200 shadow-card flex items-center justify-center text-primary-600 relative z-10 mb-5">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">{step.icon}</svg>
                  <span className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{t(step.title)}</h3>
                <p className="text-sm text-gray-600 max-w-xs leading-relaxed">{t(step.desc)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Best Courses — admin-curated, hidden entirely when none are sponsored */}
      {isFeatureEnabled('featured_content') && <section className="py-20 border-t border-surface-200">
        <div className="max-w-6xl mx-auto px-6">
          <SponsoredCoursesRow
            title={t('Best Courses')}
            subtitle={t('Handpicked courses worth a look, right now.')}
            autoAdvance
            centered
            onView={() => navigate('/store')}
            renderAction={(m) => (
              <Button
                size="sm"
                className="w-full"
                onClick={() => {
                  localStorage.setItem(PENDING_ENROLL_KEY, m.id);
                  navigate('/register');
                }}
              >
                {t('Enroll')}
              </Button>
            )}
          />
        </div>
      </section>}

      {/* Featured teachers */}
      {isFeatureEnabled('teacher_profiles') && isFeatureEnabled('featured_content') && <section className={`${hasFeaturedTeachers === false ? 'hidden' : ''} relative overflow-hidden border-t border-surface-200 bg-gradient-to-b from-white to-primary-50/60 py-20`}>
        <div className="absolute -left-32 top-12 h-72 w-72 rounded-full bg-accent-200/25 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-6">
          <FeaturedTeachersCarousel
            title={t('Valuable teachers')}
            subtitle={t('Meet outstanding educators chosen for the knowledge, care and guidance they bring to every learner.')}
            autoAdvance
            onAvailabilityChange={handleFeaturedAvailability}
          />
          <div className="mt-7 text-center">
            <Link to="/teachers" className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 transition hover:text-primary-900">
              {t('Choose your teacher')} <span className="rtl:rotate-180">→</span>
            </Link>
          </div>
        </div>
      </section>}

      {/* Features by role */}
      <section id="features" className="py-20 border-t border-surface-200 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-14">
            <h2 className="text-3xl font-display font-bold tracking-tight">{t('Everything each role needs')}</h2>
            <p className="mt-3 text-gray-600 max-w-xl mx-auto">{t('One platform, four experiences — tailored to what each person actually needs to do.')}</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ROLE_FEATURES.map((role, i) => (
              <Reveal key={role.key} delay={i * 100}>
                <div className={`group h-full bg-white rounded-2xl border border-surface-200 shadow-card p-6 flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-card-hover ring-4 ring-transparent ${role.ring}`}>
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${role.color} flex items-center justify-center text-white mb-4 transition-transform duration-300 group-hover:scale-110`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {role.icon}
                    </svg>
                  </div>
                  <h3 className="font-display font-bold text-lg mb-3">{t(role.title)}</h3>
                  <ul className="space-y-2.5 text-sm text-gray-600 flex-1">
                    {role.items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <svg className="w-4 h-4 mt-0.5 shrink-0 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{t(item)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive product tour */}
      <section id="showcase" className="relative overflow-hidden border-t border-primary-100 bg-gradient-to-br from-primary-50 via-white to-sky-50 py-20 sm:py-24">
        <div className="absolute -left-40 top-10 h-96 w-96 rounded-full bg-primary-300/30 blur-3xl" aria-hidden="true" />
        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-cyan-300/25 blur-3xl" aria-hidden="true" />
        <div
            className="absolute inset-0 opacity-[0.2]"
            style={{
              backgroundImage: 'radial-gradient(rgba(103,57,224,0.22) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
          aria-hidden="true"
        />

        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary-600">
                <span className="h-px w-8 bg-primary-500" />
                {t('Inside the platform')}
              </span>
              <h2 className="mt-4 text-3xl font-display font-bold tracking-tight text-gray-900 sm:text-4xl text-balance">
                {t('One platform. A clear view for everyone.')}
              </h2>
            </div>
            <p className="max-w-md text-sm leading-7 text-gray-600 sm:text-base">
              {t('Explore the real workflows that connect teachers, students, parents and administrators.')}
            </p>
          </Reveal>

          <Reveal>
            <div className="grid items-start gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-12">
              <div className="order-2 space-y-2 lg:order-1" role="tablist" aria-label={t('Platform tour')}>
                {SHOWCASE.map((shot, i) => {
                  const isActive = activeShowcase === i;
                  return (
                    <button
                      key={shot.file}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls="showcase-preview"
                      onClick={() => setActiveShowcase(i)}
                      className={`group w-full rounded-2xl border px-4 py-4 text-left transition-all duration-300 rtl:text-right ${
                        isActive
                          ? 'border-primary-200 bg-white shadow-[0_16px_40px_-24px_rgba(103,57,224,0.35)]'
                          : 'border-transparent bg-transparent hover:border-primary-100 hover:bg-white/60'
                      }`}
                    >
                      <span className="flex items-start gap-4">
                        <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold transition-colors ${isActive ? 'bg-primary-600 text-white' : 'bg-primary-100/70 text-primary-400 group-hover:bg-primary-100 group-hover:text-primary-600'}`}>
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={`block font-display text-sm font-semibold transition-colors sm:text-base ${isActive ? 'text-gray-900' : 'text-gray-500 group-hover:text-gray-800'}`}>
                            {t(shot.title)}
                          </span>
                          <span className={`mt-1 block text-xs leading-5 transition-all sm:text-sm ${isActive ? 'text-gray-600' : 'text-gray-400 lg:hidden'}`}>
                            {t(shot.desc)}
                          </span>
                        </span>
                        <svg className={`mt-2 h-4 w-4 shrink-0 transition-all rtl:rotate-180 ${isActive ? 'translate-x-0 text-primary-600 opacity-100' : '-translate-x-2 text-gray-400 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="order-1 lg:order-2">
                <div className="relative" id="showcase-preview" role="tabpanel">
                  <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-primary-300/35 via-transparent to-cyan-300/25 blur-2xl" aria-hidden="true" />
                  <div className="relative overflow-hidden rounded-2xl border border-gray-800/80 bg-gray-900 shadow-[0_28px_80px_-28px_rgba(58,33,128,0.45)] sm:rounded-[1.75rem]">
                    <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.06] px-4 py-3 sm:px-5">
                      <div className="flex items-center gap-1.5" aria-hidden="true">
                        <span className="h-2.5 w-2.5 rounded-full bg-red-400/90" />
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/90" />
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/90" />
                      </div>
                      <div className="hidden items-center gap-2 rounded-full bg-black/25 px-3 py-1 text-[10px] font-medium text-gray-400 sm:flex">
                        <svg className="h-3 w-3 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                        {t('Real product screen')}
                      </div>
                      <span className="font-mono text-[10px] text-gray-500">
                        {String(activeShowcase + 1).padStart(2, '0')} / {String(SHOWCASE.length).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="relative aspect-[16/10] overflow-hidden bg-surface-100">
                      <img
                        key={SHOWCASE[activeShowcase].file}
                        src={`/showcase/${SHOWCASE[activeShowcase].file}`}
                        alt={t(SHOWCASE[activeShowcase].title)}
                        className="h-full w-full animate-fade-in object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  <div className="relative mx-4 -mt-px flex items-start justify-between gap-4 rounded-b-2xl border border-t-0 border-primary-100 bg-white/90 px-5 py-4 shadow-card backdrop-blur sm:mx-8 sm:px-6">
                    <div>
                      <p className="font-display font-semibold text-gray-900">{t(SHOWCASE[activeShowcase].title)}</p>
                      <p className="mt-1 text-sm leading-6 text-gray-600">{t(SHOWCASE[activeShowcase].desc)}</p>
                    </div>
                    <span className="mt-1 hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500/15 text-primary-300 sm:flex">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" /></svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bold closing CTA band */}
      {!isAuthenticated && (
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-700 via-primary-600 to-accent-600" aria-hidden="true" />
          <div
            className="absolute inset-0 opacity-[0.15] pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '22px 22px',
            }}
            aria-hidden="true"
          />
          <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
          <Reveal className="relative max-w-4xl mx-auto px-6 py-20 text-center">
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-white text-balance">
              {t('Ready to bring your school online?')}
            </h2>
            <p className="mt-4 text-primary-100 max-w-xl mx-auto text-lg">
              {t('Free to create an account — set up your first course or link your child in minutes.')}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/register')}
                className="!bg-white !text-primary-700 hover:!bg-primary-50 shadow-card-hover"
              >
                {t('Create Free Account')}
              </Button>
              <Link
                to="/store"
                className="text-sm font-semibold text-white/90 hover:text-white transition inline-flex items-center gap-1.5"
              >
                {t('Browse courses first')}
                <svg className="w-4 h-4 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </Reveal>
        </section>
      )}

      {/* Download */}
      <section id="download" className="py-20 border-t border-surface-200 bg-white">
        <Reveal className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-display font-bold tracking-tight">{t('Take it with you')}</h2>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            {t('The Android app is ready to download today; iOS is on the way. Same courses, messaging and progress tracking as the website.')}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-gray-900 text-white opacity-90 cursor-not-allowed select-none">
              <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" /></svg>
              <div className="text-left">
                <p className="text-[11px] uppercase tracking-wide opacity-70 leading-none">{t('Coming soon on')}</p>
                <p className="font-semibold leading-tight">App Store</p>
              </div>
            </div>
            <a
              href="/downloads/lmcid-android.apk?v=1.7"
              download
              className="flex items-center gap-3 px-5 py-3 rounded-xl bg-gray-900 text-white hover:bg-gray-800 transition"
            >
              <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M3 20.5V3.5a1 1 0 011.53-.85l13.6 8.5a1 1 0 010 1.7l-13.6 8.5A1 1 0 013 20.5z" /></svg>
              <div className="text-left">
                <p className="text-[11px] uppercase tracking-wide opacity-70 leading-none">{t('Direct download for')}</p>
                <p className="font-semibold leading-tight">Android (.apk) · v1.7</p>
              </div>
            </a>
          </div>
          <p className="mt-4 text-xs text-gray-400 max-w-md mx-auto">
            {t("Not on Google Play yet — you'll need to allow installs from outside the Play Store when opening the file.")}
          </p>
        </Reveal>
      </section>

      <PlatformFooter />
    </div>
  );
}
